/**
 * Stat (KPI) -komponentin puhdas muotoilulogiikka (AB-WP6.6). Ei DOMia,
 * ei Intl-riippuvuutta (deterministinen testeissä ja ympäristöstä riippumatta).
 * runtime.ts:n wireStat käyttää näitä; palvelin renderöi vain shellin +
 * raakakonfigin (kuten pagination), joten muotoilua ei ole kahdessa paikassa.
 */

import type { MessageLocale } from './messages';
import {
  formatCurrency, formatDecimal, formatInteger, formatPercent, numberStyle,
} from './numberFormat';

export type StatFormat = 'number' | 'currency' | 'percent' | 'bytes' | 'decimal' | 'text';
export type DeltaDirection = 'up' | 'down' | 'flat';

const FORMATS = new Set<StatFormat>(['number', 'currency', 'percent', 'bytes', 'decimal', 'text']);

/** Ihmisluettava tavukoko (1258291 → "1.2 MB" / "1,2 MB"). */
export function humanizeBytes(value: number, locale: MessageLocale = 'en'): string {
  const style = numberStyle(locale);
  const neg = value < 0;
  let n = Math.abs(value);
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  let i = 0;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i += 1;
  }
  const rounded = i === 0 ? Math.round(n) : Math.round(n * 10) / 10;
  return (neg ? '-' : '') + formatDecimal(rounded, style, null) + ' ' + units[i];
}

/**
 * Muotoile arvo valitulla formaatilla. Tuntematon formaatti → number.
 *
 * `text` (WP-SF5, löydös T-D) näyttää arvon sellaisenaan. Sitä ennen sidottu
 * merkkijono — esim. kesto "7 h 30 min", tunnus tai tila — renderöityi
 * **nollana**, koska numeroformaatit aloittavat `Number.isFinite`-tarkistuksella.
 * Nolla on uskottava tunnusluku, joten vika ei näyttänyt vialta.
 *
 * Formaatti on eksplisiittinen eikä arvosta pääteltävä: sama komponentti ei saa
 * muotoilla tai olla muotoilematta sen mukaan mitä sidonta sattui palauttamaan,
 * koska silloin "12" olisi ikuinen rajatapaus.
 */
export function formatStatValue(
  value: number | string, format: string, locale: MessageLocale = 'en',
): string {
  const fmt = FORMATS.has(format as StatFormat) ? (format as StatFormat) : 'number';
  if (fmt === 'text') return value == null ? '' : String(value);
  if (typeof value !== 'number' || !Number.isFinite(value)) value = 0;
  const style = numberStyle(locale);
  switch (fmt) {
    case 'currency':
      return formatCurrency(value, style);
    case 'percent':
      // Syöte on jo näyttöyksikkö: 12 → "12%" / "12 %", ei 1200 %.
      return formatPercent(value, style);
    case 'bytes':
      return humanizeBytes(value, locale);
    case 'decimal':
      return formatDecimal(value, style, 2);
    case 'number':
    default:
      return formatInteger(value, style);
  }
}

/** Delta-suunta: auto johtaa etumerkistä, muuten pakotettu arvo. */
export function resolveDeltaDirection(delta: number, mode: string): DeltaDirection {
  if (mode === 'up' || mode === 'down' || mode === 'flat') return mode;
  if (!Number.isFinite(delta) || delta === 0) return 'flat';
  return delta > 0 ? 'up' : 'down';
}

/** Delta-teksti etumerkkiprefiksillä (+/-) ja formaatilla; iteisarvo muotoillaan. */
export function formatDelta(
  delta: number, format: string, locale: MessageLocale = 'en',
): string {
  if (!Number.isFinite(delta)) delta = 0;
  const sign = delta > 0 ? '+' : delta < 0 ? '-' : '';
  return sign + formatStatValue(Math.abs(delta), format, locale);
}
